import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { checkoutUrlFor, usePaymentsStatus } from "../lib/payments";
import type { Marketer, PaymentLink, Plan, Product } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  OrderStatusBadge,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
  formatMoney,
} from "../components/ui";

export function PaymentLinks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [creating, setCreating] = useState(false);
  const { cardsAccepted, loading: checkingPayments } = usePaymentsStatus();

  const queryClient = useQueryClient();

  const links = useQuery({
    queryKey: ["payment-links"],
    queryFn: () => api<PaymentLink[]>("/api/v1/admin/payment-links"),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/admin/payment-links/${id}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["payment-links"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment links"
        lede="One link per prospect. Whoever the link belongs to is who gets the commission."
        action={
          <Button variant="primary" onClick={() => setCreating((value) => !value)}>
            {creating ? "Cancel" : "New link"}
          </Button>
        }
      />

      {/* Shown only while it is true. A warning that outlives the problem is
          how people learn to stop reading warnings. */}
      {!cardsAccepted && !checkingPayments && (
        <p className="rounded-md border border-pending/40 bg-pending/10 px-3 py-2 text-sm text-pending">
          Card payments are not switched on, so these links cannot take money yet. Links created now
          keep working — the attribution is already recorded against them.
        </p>
      )}

      {remove.isError && <ErrorNote>{(remove.error as Error).message}</ErrorNote>}

      {creating && <NewLink isAdmin={isAdmin} onClose={() => setCreating(false)} />}

      <Card>
        {links.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : links.isError ? (
          <div className="p-4">
            <ErrorNote>Could not load links. Reload to try again.</ErrorNote>
          </div>
        ) : links.data.length === 0 ? (
          <EmptyState>No links yet.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Prospect</Th>
                  <Th>Product</Th>
                  <Th>Price</Th>
                  {isAdmin && <Th>Marketer</Th>}
                  <Th>Outcome</Th>
                  <Th>Expires</Th>
                  <Th>Link</Th>
                  <Th className="text-right">Remove</Th>
                </tr>
              </thead>
              <tbody>
                {links.data.map((link) => (
                  <tr key={link.id} className="border-b border-hairline last:border-0">
                    <Td label="Prospect">
                      <span className="block">{link.customerName ?? "Not named"}</span>
                      {link.customerEmail != null && (
                        <span className="block text-xs text-dim">{link.customerEmail}</span>
                      )}
                    </Td>
                    <Td label="Product" className="text-muted">
                      <span className="block">{link.product.name}</span>
                      <span className="block text-xs text-dim">{link.plan.name}</span>
                    </Td>
                    <Td label="Price" className="tabular text-muted">
                      {formatMoney(link.plan.priceCents, link.plan.currency)}
                      <span className="text-dim"> / {link.plan.interval}</span>
                    </Td>
                    {isAdmin && (
                      <Td label="Marketer" className="text-muted">
                        {link.marketer === null ? (
                          <span className="text-dim">Direct</span>
                        ) : (
                          link.marketer.name
                        )}
                      </Td>
                    )}
                    <Td label="Outcome">
                      {link.order === null ? (
                        <Badge tone="quiet">Not used</Badge>
                      ) : (
                        <Link to={`/orders/${link.order.id}`} className="inline-block">
                          <OrderStatusBadge status={link.order.status} />
                        </Link>
                      )}
                    </Td>
                    <Td label="Expires" className="text-muted">
                      {link.expiresAt === null ? (
                        <span className="text-dim">Never</span>
                      ) : (
                        formatDate(link.expiresAt)
                      )}
                    </Td>
                    <Td label="Link">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          void navigator.clipboard.writeText(checkoutUrlFor(link.token))
                        }
                      >
                        Copy
                      </Button>
                    </Td>
                    {/* A quote that is no longer on the table should stop being
                        a working checkout page. A used link stays: it is the
                        attribution for real money, and the server refuses. */}
                    <Td label="Remove" className="text-right">
                      {link.order === null && link.usedAt == null ? (
                        <Button
                          variant="ghost"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove this link${
                                  link.customerName == null ? "" : ` for ${link.customerName}`
                                }? Anyone holding it will get a dead page.`,
                              )
                            ) {
                              remove.mutate(link.id);
                            }
                          }}
                        >
                          Remove
                        </Button>
                      ) : (
                        <span className="text-xs text-dim">Used</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}

/**
 * A marketer never picks whose link this is — the server stamps their own id
 * regardless of what is sent, so the field is not offered to them at all.
 */
function NewLink({ isAdmin, onClose }: { isAdmin: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState("");
  const [planId, setPlanId] = useState("");
  const [marketerId, setMarketerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deployment, setDeployment] = useState<"self_hosted" | "managed">("self_hosted");
  const [hostnames, setHostnames] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("14");
  const [oneTime, setOneTime] = useState("");
  const [monthly, setMonthly] = useState("");
  const [graceMonths, setGraceMonths] = useState("");
  const [created, setCreated] = useState<string | null>(null);

  const products = useQuery({
    queryKey: ["products"],
    queryFn: () => api<Product[]>("/api/v1/admin/products"),
  });
  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: () => api<Plan[]>("/api/v1/admin/plans"),
  });
  const marketers = useQuery({
    queryKey: ["marketers"],
    queryFn: () => api<Marketer[]>("/api/v1/admin/marketers"),
    enabled: isAdmin,
  });

  const planOptions = (plans.data ?? []).filter((plan) => plan.productId === productId);
  const plan = planOptions.find((option) => option.id === planId) ?? null;

  const create = useMutation({
    mutationFn: () =>
      api<PaymentLink>("/api/v1/admin/payment-links", {
        method: "POST",
        body: {
          // "direct" is the deliberate choice of nobody, and is not an id.
          ...(isAdmin && marketerId.length > 0 && marketerId !== "direct"
            ? { marketerId }
            : {}),
          productId,
          planId,
          ...(customerName.trim().length === 0 ? {} : { customerName: customerName.trim() }),
          ...(customerEmail.trim().length === 0 ? {} : { customerEmail: customerEmail.trim() }),
          ...(customerPhone.trim().length === 0 ? {} : { customerPhone: customerPhone.trim() }),
          ...(oneTime.trim() === "" ? {} : { oneTimeCents: toCents(oneTime) }),
          ...(monthly.trim() === "" ? {} : { monthlyCents: toCents(monthly) }),
          ...(graceMonths === "" ? {} : { maintenanceGraceMonths: Number(graceMonths) }),
          deployment,
          hostnames: hostnames
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
          ...(expiresInDays.trim().length === 0
            ? {}
            : { expiresInDays: Number(expiresInDays) }),
        },
      }),
    onSuccess: (link) => {
      setCreated(checkoutUrlFor(link.token));
      void queryClient.invalidateQueries({ queryKey: ["payment-links"] });
    },
  });

  if (created !== null) {
    return (
      <Card title="Link created">
        <div className="space-y-4 p-4">
          <p className="text-sm text-muted">
            Send this to the prospect. It can be copied again from the table at any time.
          </p>
          <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-ink">
            {created}
          </code>
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => void navigator.clipboard.writeText(created)}>
              Copy link
            </Button>
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title="New payment link">
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Product">
            <Select
              value={productId}
              required
              onChange={(event) => {
                setProductId(event.target.value);
                setPlanId("");
              }}
            >
              <option value="">Select a product</option>
              {(products.data ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Plan">
            <Select
              value={planId}
              required
              disabled={productId.length === 0}
              onChange={(event) => setPlanId(event.target.value)}
            >
              <option value="">
                {productId.length === 0 ? "Pick a product first" : "Select a plan"}
              </option>
              {planOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name} — {formatMoney(option.priceCents, option.currency)} /{" "}
                  {option.interval}
                </option>
              ))}
            </Select>
          </Field>

          {/* One link, both halves of the sale: the build is charged when the
              customer pays, and maintenance starts whenever the deal says. */}
          <Field
            label="One-time price"
            hint={priceHint("Build", plan?.oneTimePriceCents ?? null, plan?.floorPriceCents ?? null, plan?.currency ?? null)}
          >
            <Input
              inputMode="decimal"
              value={oneTime}
              placeholder={placeholderFor(plan?.oneTimePriceCents ?? null)}
              onChange={(event) => setOneTime(event.target.value)}
            />
          </Field>

          <Field
            label={`Recurring price (per ${plan?.interval ?? "month"})`}
            hint={priceHint("Maintenance", plan?.priceCents ?? null, plan?.floorMonthlyCents ?? null, plan?.currency ?? null)}
          >
            <Input
              inputMode="decimal"
              value={monthly}
              placeholder={placeholderFor(plan?.priceCents ?? null)}
              onChange={(event) => setMonthly(event.target.value)}
            />
          </Field>

          <Field
            label="Maintenance starts"
            hint={
              plan === null
                ? "Pick a plan to see its default."
                : graceMonths === ""
                  ? `Plan default: ${graceLabel(plan.maintenanceGraceMonths)}.`
                  : "Overrides the plan for this deal only."
            }
          >
            <Select value={graceMonths} onChange={(event) => setGraceMonths(event.target.value)}>
              <option value="">Whatever the plan says</option>
              {GRACE_OPTIONS.map((option) => (
                <option key={option.months} value={String(option.months)}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          {isAdmin && (
            <Field
              label="Marketer"
              hint="Who earns the commission. A direct sale earns none, which is why it is a choice rather than a blank."
            >
              <Select
                value={marketerId}
                required
                onChange={(event) => setMarketerId(event.target.value)}
              >
                <option value="">Select who this is for</option>
                <option value="direct">Direct sale — no commission</option>
                {(marketers.data ?? [])
                  .filter((marketer) => marketer.active)
                  .map((marketer) => (
                    <option key={marketer.id} value={marketer.id}>
                      {marketer.name}
                    </option>
                  ))}
              </Select>
            </Field>
          )}

          <Field label="Expires in days" hint="Leave empty for a link that never expires.">
            <Input
              type="number"
              min={1}
              max={90}
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(event.target.value)}
            />
          </Field>

          <Field label="Prospect name">
            <Input
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
            />
          </Field>

          <Field label="Prospect email" hint="Where the receipt and licence will go.">
            <Input
              type="email"
              value={customerEmail}
              onChange={(event) => setCustomerEmail(event.target.value)}
            />
          </Field>

          <Field label="Prospect phone">
            <Input
              value={customerPhone}
              onChange={(event) => setCustomerPhone(event.target.value)}
            />
          </Field>

          <Field label="Deployment">
            <Select
              value={deployment}
              onChange={(event) => setDeployment(event.target.value as "self_hosted" | "managed")}
            >
              <option value="self_hosted">Customer hosts it</option>
              <option value="managed">We host it</option>
            </Select>
          </Field>
        </div>

        <Field label="Allowed hostnames" hint="One per line. Can be filled in later on the order.">
          <textarea
            value={hostnames}
            rows={2}
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 tabular text-ink focus:border-accent"
            onChange={(event) => setHostnames(event.target.value)}
          />
        </Field>

        {create.isError && <ErrorNote>{(create.error as Error).message}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create link"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * How long after checkout maintenance begins. Whole months, because that is how
 * these deals are actually agreed — "starts next month", "care begins in a
 * year" — and because Stripe caps a subscription trial at two years.
 */
const GRACE_OPTIONS = [
  { months: 0, label: "Immediately, at checkout" },
  { months: 1, label: "Next month" },
  { months: 3, label: "In 3 months" },
  { months: 6, label: "In 6 months" },
  { months: 12, label: "After a year" },
  { months: 24, label: "After 2 years" },
] as const;

function graceLabel(months: number | null): string {
  if (months === null || months === 0) return "immediately";
  return months === 1 ? "next month" : `in ${months} months`;
}

/** Typed in whole currency; the API deals in cents and nothing else. */
function toCents(value: string): number {
  return Math.round(Number(value.replace(/[^0-9.]/g, "")) * 100);
}

function placeholderFor(cents: number | null): string {
  return cents === null ? "List price" : String(Math.round(cents / 100));
}

/** Says what is on offer and what the floor is, so a quote is not guesswork. */
function priceHint(
  what: string,
  listCents: number | null,
  floorCents: number | null,
  currency: string | null,
): string {
  if (listCents === null && floorCents === null) return `${what}. Leave empty for list price.`;
  const list = listCents === null ? null : `list ${formatMoney(listCents, currency)}`;
  const floor = floorCents === null ? null : `floor ${formatMoney(floorCents, currency)}`;
  return [list, floor].filter((part) => part !== null).join(", ");
}
